// File Message Component
// Following existing patterns from MessageItem

import React, { useCallback } from 'react';
import { FileMessageProps } from '../../../types/messaging';
import {
  FileMessageContainer,
  FilePreview,
  FileIcon,
  FileInfo,
  FileName,
  FileSize,
  FileActions,
  FileActionButton,
  DownloadButton
} from '../../../styles/Messaging/MessageTypes.styles';

export const FileMessage: React.FC<FileMessageProps> = ({
  message,
  isOwn,
  onAction
}) => {
  const handleDownload = useCallback(() => {
    const attachment = message.attachments?.[0];
    if (attachment?.url) {
      const link = document.createElement('a');
      link.href = attachment.url;
      link.download = attachment.filename || 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [message.attachments]);

  const handlePreview = useCallback(() => {
    const attachment = message.attachments?.[0];
    if (attachment?.url) {
      window.open(attachment.url, '_blank');
    }
  }, [message.attachments]);

  const handleShare = useCallback(() => {
    const attachment = message.attachments?.[0];
    if (navigator.share && attachment?.url) {
      navigator.share({
        title: attachment.filename || 'Shared File',
        url: attachment.url
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(attachment?.url || '').catch(console.error);
    }
  }, [message.attachments]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (filename: string, mimeType?: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    
    // Document types
    if (['pdf'].includes(extension || '') || mimeType?.includes('pdf')) {
      return '📄';
    }
    if (['doc', 'docx'].includes(extension || '') || mimeType?.includes('word')) {
      return '📝';
    }
    if (['xls', 'xlsx'].includes(extension || '') || mimeType?.includes('excel')) {
      return '📊';
    }
    if (['ppt', 'pptx'].includes(extension || '') || mimeType?.includes('powerpoint')) {
      return '📈';
    }
    
    // Archive types
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension || '')) {
      return '🗜️';
    }
    
    // Code types
    if (['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'xml'].includes(extension || '')) {
      return '💻';
    }
    
    // Default
    return '📎';
  };

  const attachment = message.attachments?.[0];
  if (!attachment) {
    return (
      <FileMessageContainer $isOwn={isOwn}>
        <div style={{ padding: '1rem', textAlign: 'center', color: '#6c757d' }}>
          File not available
        </div>
      </FileMessageContainer>
    );
  }

  const { filename, size, mimeType, url } = attachment;
  const fileIcon = getFileIcon(filename, mimeType);

  return (
    <FileMessageContainer $isOwn={isOwn}>
      <FilePreview>
        <FileIcon>{fileIcon}</FileIcon>
        
        <FileInfo>
          <FileName>{filename}</FileName>
          <FileSize>{formatFileSize(size)}</FileSize>
        </FileInfo>
        
        <FileActions>
          <FileActionButton onClick={handlePreview} title="Preview">
            👁️
          </FileActionButton>
          <FileActionButton onClick={handleShare} title="Share">
            🔗
          </FileActionButton>
        </FileActions>
      </FilePreview>
      
      <DownloadButton onClick={handleDownload}>
        ⬇️ Download
      </DownloadButton>
      
      {message.content && (
        <div style={{ 
          marginTop: '0.5rem', 
          padding: '0.5rem', 
          background: 'rgba(0, 0, 0, 0.05)', 
          borderRadius: '4px',
          fontSize: '0.875rem'
        }}>
          {message.content}
        </div>
      )}
    </FileMessageContainer>
  );
};
